/* eslint-disable array-callback-return */
/* eslint-disable max-len */
import React from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { format, isValid } from "date-fns";
import { Formik } from "formik";
import * as Yup from "yup";
import Grid from "@mui/material/Grid";
import {
  Input,
  AsyncSelectAutoComplete,
  SelectAutocomplete,
  AutoSaveField as Field,
  DatePicker
} from "../../../../../../../../components/Form";
import { DATE_FORMAT } from "../../../../../../../../constants";
import {
  SPECIAL_CHARACTERS_REGEX,
  NUMBER_REGEX,
  DECIMAL_TWO_DIGIT_DECIMAL_REGEX
} from "../../../../../../../../utils/validation";
import { getInvalidMessage } from "../../../../../../../../utils/getValidationMessage";
import { FormContainer } from "../../styled";
import { receiveDatalistSearchPromisify } from "../../../../../../../../store/actions/appConfig";
import { getUAEBankName } from "../../../../../../../../store/selectors/appConfig";
import { getObjectUsingDatalistValue } from "../../../../../../../../utils/helper";
import { ScrollToError } from "components/ScrollToError";

const primaryBorrowerUAESchema = () =>
  Yup.object().shape({
    LoanFacilityAvailed: Yup.date()
      .nullable()
      .max(new Date(), getInvalidMessage("Date Loan facility availed"))
      .typeError(getInvalidMessage("Date Loan facility availed")),
    tenurePaid: Yup.string()
      .max(13, "Maximum 13 characters allowed")
      .matches(NUMBER_REGEX, getInvalidMessage("EMI paid/ tenure paid")),
    emiAmount: Yup.string()
      .max(13, "Maximum 13 characters allowed")
      .matches(DECIMAL_TWO_DIGIT_DECIMAL_REGEX, getInvalidMessage("EMI amount")),
    emiClearing: Yup.date()
      .nullable()
      .typeError(getInvalidMessage("EMI Clearing")),
    clearingBank: Yup.string().max(50, "Maximum 50 characters allowed"),
    takeOverBank: Yup.string().max(50, "Maximum 50 characters allowed"),
    outstandingOrCleanExposure: Yup.string()
      .max(13, "Maximum 13 characters allowed")
      .matches(NUMBER_REGEX, getInvalidMessage("Outstanding or Clean Exposure")),
    facilityLetter: Yup.string().max(10, "Maximum 10 characters allowed"),
    previousRblUtilised: Yup.string()
      .max(255, "Maximum 255 characters allowed")
      .matches(
        SPECIAL_CHARACTERS_REGEX,
        getInvalidMessage("Comment on how was the previous RBL utilised")
      ),
    otherLoansUtilised: Yup.string()
      .max(255, "Maximum 255 characters allowed")
      .matches(
        SPECIAL_CHARACTERS_REGEX,
        getInvalidMessage(
          "Comment on how was Other Bank business loans utilised since previous RBL or 12 months( whichever was most recent)"
        )
      )
  });
const initialValues = {
  LoanFacilityAvailed: "",
  tenurePaid: "",
  emiAmount: "",
  emiClearing: "",
  clearingBank: "",
  takeOverBank: "",
  outstandingOrCleanExposure: "",
  facilityLetter: "",
  previousRblUtilised: "",
  otherLoansUtilised: ""
};
export const PrimaryBorrowerUAE = ({ handleContinue, index }) => {
  const dataList = useSelector(getUAEBankName);
  const dispatch = useDispatch();
  const changeDateProspectHandler = (_, value, path) =>
    isValid(value) && { [path]: format(value, DATE_FORMAT) };
  const basePath = `roProspect.organizationInfoRO.otherBankPrimaryBorrowerRelationship.otherUAEBankRelationship[${index}]`;
  const getDatalistSearch = data => {
    return dispatch(receiveDatalistSearchPromisify(data));
  };
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={primaryBorrowerUAESchema}
      onSubmit={handleContinue}
      validateOnChange={false}
    >
      {({ values }) => {
        let clearingBank = {};
        let takeOverBank = {};
        if (dataList) {
          clearingBank = getObjectUsingDatalistValue(dataList, values.clearingBank);
          takeOverBank = getObjectUsingDatalistValue(dataList, values.takeOverBank);
        }
        return (
          <FormContainer>
            <ScrollToError />
            <Grid container spacing={3}>
              <Grid item sm={6} xs={12}>
                <Field
                  name="LoanFacilityAvailed"
                  path={`${basePath}.loanAvailedDate`}
                  label="Date Loan facility availed"
                  placeholder="Date Loan facility availed"
                  component={DatePicker}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  maxDate={new Date()}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="tenurePaid"
                  path={`${basePath}.emiPaid`}
                  label="EMI paid/ tenure paid"
                  placeholder="EMI paid/ tenure paid"
                  component={Input}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="emiAmount"
                  path={`${basePath}.emiAmount`}
                  label="EMI amount"
                  placeholder="EMI amount"
                  component={Input}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="emiClearing"
                  path={`${basePath}.emiClearing`}
                  label="EMI Clearing"
                  placeholder="EMI Clearing"
                  component={DatePicker}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  disableFuture={false}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="clearingBank"
                  path={`${basePath}.clearingBank`}
                  label="Clearing Bank"
                  placeholder="Clearing Bank"
                  component={AsyncSelectAutoComplete}
                  getDatalistSearch={getDatalistSearch}
                  codeType="UAEB"
                  defaultValue={clearingBank}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="takeOverBank"
                  path={`${basePath}.takeOverBank`}
                  label="Take Over Bank"
                  placeholder="Take Over Bank"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  component={AsyncSelectAutoComplete}
                  getDatalistSearch={getDatalistSearch}
                  codeType="UAEB"
                  defaultValue={takeOverBank}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <Field
                  name="outstandingOrCleanExposure"
                  path={`${basePath}.outstandingExposure`}
                  label="Outstanding or Clean Exposure"
                  placeholder="Outstanding or Clean Exposure"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  component={Input}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <Field
                  name="facilityLetter"
                  path={`${basePath}.facilityLetterProvided`}
                  label="Facility Letter for all trade finance and business documents provided"
                  placeholder="Facility Letter for all trade finance and business documents provided"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  component={SelectAutocomplete}
                  datalistId="flagWithNa"
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <Field
                  name="previousRblUtilised"
                  path={`${basePath}.howPreviousRBLUtilised`}
                  label="Comment on how was the previous RBL utilised"
                  placeholder="Comment on how was the previous RBL utilised"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  component={Input}
                  multiline
                  rows="4"
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <Field
                  name="otherLoansUtilised"
                  path={`${basePath}.howOtherBankLoanUtilised`}
                  label="Comment on how was Other Bank business loans utilised since previous RBL or 12 months( whichever was most recent)"
                  placeholder="Comment on how was Other Bank business loans utilised since previous RBL or 12 months( whichever was most recent)"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  component={Input}
                  multiline
                  rows="4"
                />
              </Grid>
            </Grid>
          </FormContainer>
        );
      }}
    </Formik>
  );
};

PrimaryBorrowerUAE.propTypes = {
  /**
   * Function to handle form submission and continue to next step
   */
  handleContinue: PropTypes.func.isRequired,

  /**
   * Index of the bank relationship in the array, used for path construction
   */
  index: PropTypes.number.isRequired
};
